import { useCallback, useEffect, useMemo, useState } from "react";
import { createOpsCrossLaunch } from "../../api/auth";
import SuperAdminMarketplaceSection from "../../components/superadmin/SuperAdminMarketplaceSection";
import { useBrand } from "../../context/BrandContext";
import { buildHostUrl } from "../../config/hostRouting";
import { fetchAllBusinesses, fetchAllSalons } from "../../api/superadmin";
import MarketplaceShell from "./MarketplaceShell";

const SUPERADMIN_SELECTED_BUSINESS_KEY = "sbin_superadmin_selected_business_id";

function readStoredSelectedBusinessId() {
  if (typeof window === "undefined") return "";
  const raw = window.localStorage.getItem(SUPERADMIN_SELECTED_BUSINESS_KEY);
  if (!raw) return "";
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : "";
}

function persistSelectedBusinessId(businessId) {
  if (typeof window === "undefined") return;
  const numeric = Number(businessId);
  if (!Number.isFinite(numeric) || numeric <= 0) {
    window.localStorage.removeItem(SUPERADMIN_SELECTED_BUSINESS_KEY);
    return;
  }
  window.localStorage.setItem(SUPERADMIN_SELECTED_BUSINESS_KEY, String(numeric));
}

function StatCard({ label, value, caption }) {
  return (
    <article className="rounded-[1.75rem] border border-slate-200 bg-white p-5 shadow-sm">
      <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">
        {label}
      </p>
      <p className="mt-3 text-3xl font-semibold text-slate-900">{value}</p>
      <p className="mt-2 text-sm text-slate-600">{caption}</p>
    </article>
  );
}

export default function MarketplaceSuperAdminPage() {
  const brand = useBrand();
  const [businesses, setBusinesses] = useState([]);
  const [allSalons, setAllSalons] = useState([]);
  const [selectedBusinessId, setSelectedBusinessId] = useState(() =>
    readStoredSelectedBusinessId()
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError("");
    Promise.all([fetchAllBusinesses(), fetchAllSalons()])
      .then(([businessRows, salonRows]) => {
        if (!mounted) return;
        setBusinesses(Array.isArray(businessRows) ? businessRows : []);
        setAllSalons(Array.isArray(salonRows) ? salonRows : []);
      })
      .catch((err) => {
        if (!mounted) return;
        setBusinesses([]);
        setAllSalons([]);
        setError(err?.message || "Unable to load marketplace super-admin data.");
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  const selectedBusinessName = useMemo(() => {
    const businessId = Number(selectedBusinessId);
    if (!Number.isFinite(businessId) || businessId <= 0) {
      return "Platform-wide scope";
    }
    return (
      businesses.find((business) => Number(business?.id || 0) === businessId)?.name ||
      `Business ID ${businessId}`
    );
  }, [businesses, selectedBusinessId]);

  const scopedSalonCount = useMemo(() => {
    const businessId = Number(selectedBusinessId);
    if (!Number.isFinite(businessId) || businessId <= 0) {
      return allSalons.length;
    }
    return allSalons.filter(
      (salon) => Number(salon?.business_id || 0) === businessId
    ).length;
  }, [allSalons, selectedBusinessId]);

  const opsSuperAdminUrl = useMemo(
    () => buildHostUrl(brand.opsHost, "/superadmin"),
    [brand.opsHost]
  );
  const opsSetupUrl = useMemo(
    () => buildHostUrl(brand.opsHost, "/setup"),
    [brand.opsHost]
  );

  const handleBusinessChange = useCallback((nextValue) => {
    setSelectedBusinessId(nextValue || "");
    persistSelectedBusinessId(nextValue);
  }, []);

  const launchOpsPath = useCallback(
    async (nextPath, fallbackUrl) => {
      if (typeof window === "undefined") return;
      try {
        const launch = await createOpsCrossLaunch(nextPath);
        const resolvedPath = String(launch?.next_path || "").trim() || nextPath;
        const targetUrl = new URL(buildHostUrl(brand.opsHost, resolvedPath));
        if (launch?.launch_token) {
          targetUrl.searchParams.set("launch_token", launch.launch_token);
        }
        window.location.assign(targetUrl.toString());
      } catch (err) {
        console.warn("Unable to create Ellyanna cross launch", err);
        window.location.assign(fallbackUrl);
      }
    },
    [brand.opsHost]
  );

  const handleOpenBookingConsole = useCallback(() => {
    void launchOpsPath("/superadmin", opsSuperAdminUrl);
  }, [launchOpsPath, opsSuperAdminUrl]);

  const handleOpenSetup = useCallback(
    (event) => {
      event.preventDefault();
      void launchOpsPath("/setup", opsSetupUrl);
    },
    [launchOpsPath, opsSetupUrl]
  );

  const handleInspectSalon = useCallback(() => {
    handleOpenBookingConsole();
  }, [handleOpenBookingConsole]);

  return (
    <MarketplaceShell>
      <div className="space-y-6">
        <section className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm">
          <div className="grid gap-6 bg-[#2f3566] px-6 py-6 text-white lg:grid-cols-[1.05fr,0.95fr]">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.24em] text-rose-200">
                MyBoothShare Super Admin
              </p>
              <h1 className="mt-3 text-3xl font-semibold">
                Marketplace Platform Control
              </h1>
              <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-200 md:text-base">
                Run marketplace-wide oversight from MyBoothShare without dropping into the
                Booking shell first. Use this page for inventory readiness, booth health,
                owner support, and platform investigation.
              </p>
              <div className="mt-5 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={handleOpenBookingConsole}
                  className="rounded-full bg-white px-4 py-2 text-sm font-semibold text-slate-900 transition hover:bg-slate-100"
                >
                  Open Booking Super Admin
                </button>
                <a
                  href={opsSetupUrl}
                  onClick={handleOpenSetup}
                  className="rounded-full border border-white/20 bg-white/10 px-4 py-2 text-sm font-semibold text-white transition hover:bg-white/15"
                >
                  Open Ellyanna Setup
                </a>
              </div>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <StatCard
                label="Businesses"
                value={loading ? "..." : businesses.length}
                caption="Marketplace business groups currently visible to super admin."
              />
              <StatCard
                label="Salons"
                value={loading ? "..." : scopedSalonCount}
                caption="Salons in scope for the current business filter."
              />
              <StatCard
                label="Scope"
                value={selectedBusinessId ? "Focused" : "Global"}
                caption={selectedBusinessName}
              />
              <article className="rounded-[1.75rem] border border-white/10 bg-white/5 p-5 shadow-sm">
                <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-rose-200">
                  Support Flow
                </p>
                <p className="mt-3 text-xl font-semibold text-white">
                  Marketplace first
                </p>
                <p className="mt-2 text-sm leading-6 text-slate-200">
                  Stay here for marketplace governance. Jump to Ellyanna only for salon
                  setup, user recovery, or Booking-only support tasks.
                </p>
              </article>
            </div>
          </div>
        </section>

        <section className="rounded-[1.75rem] border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                Business Scope
              </p>
              <h2 className="mt-2 text-xl font-semibold text-slate-900">
                Filter marketplace oversight
              </h2>
              <p className="mt-2 text-sm text-slate-600">
                Narrow the platform view to one business when you need to work a specific
                host group. Leave it clear for network-wide operations.
              </p>
            </div>
            <div className="flex w-full flex-col gap-3 lg:w-auto lg:flex-row lg:items-center">
              <label
                className="text-sm font-medium text-slate-700"
                htmlFor="marketplace-superadmin-business"
              >
                Business scope
              </label>
              <select
                id="marketplace-superadmin-business"
                value={selectedBusinessId}
                onChange={(event) => handleBusinessChange(event.target.value)}
                className="min-w-[260px] rounded-full border border-slate-300 px-4 py-2 text-sm text-slate-700 outline-none transition focus:border-rose-400 focus:ring-2 focus:ring-rose-200"
              >
                <option value="">All businesses</option>
                {businesses.map((business) => (
                  <option key={business.id} value={String(business.id)}>
                    {business.name || `Business ${business.id}`}
                  </option>
                ))}
              </select>
              {selectedBusinessId ? (
                <button
                  type="button"
                  onClick={() => handleBusinessChange("")}
                  className="rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                >
                  Clear scope
                </button>
              ) : null}
            </div>
          </div>
          {error ? (
            <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              {error}
            </div>
          ) : null}
        </section>

        <SuperAdminMarketplaceSection
          businesses={businesses}
          allSalons={allSalons}
          selectedBusinessId={selectedBusinessId}
          onChangeBusinessId={handleBusinessChange}
          onInspectSalon={handleInspectSalon}
          inspectSalonLabel="Open in Booking"
        />
      </div>
    </MarketplaceShell>
  );
}
